<template>
  <div class="as-main-layout">
    <v-app-bar
      app
      dark
      class="as-header mx-auto"
      height="64px"
    >
      <v-app-bar-nav-icon 
          class="d-block d-sm-none" 
          @click="drawer = !drawer"
      >
      </v-app-bar-nav-icon>

      <v-spacer class="d-sm-none"></v-spacer>

      <v-toolbar-title>
          <router-link 
              :to="getLocalisedUrl('/')"
              @click.native="onLogoClick">
            <app-logo :show-tagline="true" />
          </router-link>
      </v-toolbar-title>

      <v-spacer></v-spacer>


      <!-- Desktop menu -->
      <div class="d-none d-sm-block">
          
          <!-- Language selector (separate from account menu, for all users) -->
          <v-menu
              bottom
              left
              offset-y
              eager
              class="mr-2"
          >
            <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  plain
                >
                {{ languge }}
                </v-btn>
            </template>

            <v-list class="text-right">
                <v-list-item-group>
                <v-list-item 
                    v-for="(item, index) in languages"
                    :key="index"
                    >
                    <v-btn
                      @click="changeLang(item.value)"
                      tile
                      plain
                      :class="item.value === languge ? 'as-active-language' : ''"
                    >
                    {{ item.text }}
                    </v-btn>
                </v-list-item>
                </v-list-item-group>
            </v-list>
          </v-menu>

          <!-- Auth buttons for non-logged in users -->
          <div class="d-inline-block" v-if="!isLoggedIn">
              <v-btn
                  text
                  color="white"
                  class="mr-2"
                  @click="goToAuth('signin')"
              >
                  {{ $t('layout.menu.login') }}
              </v-btn>
              <v-btn
                  color="var(--color-pallet-1)"
                  dark
                  rounded
                  @click="goToAuth('signup')"
                  class="signup-btn"
              >
                  {{ $t('layout.menu.signup') }}
              </v-btn>
          </div>

      </div>

      <!-- Account button (desktop: name + chevron; mobile: icon). Menu: languages | accounts | logout. -->
      <div v-if="isLoggedIn" class="d-inline-block as-header-account-wrap">
        <user-account-menu
          variant="light"
          :show-language="isMobileView"
        />
      </div>

      <!-- Mobile: three-dot menu when not logged in (language + login/signup) -->
      <div class="d-block d-sm-none as-mobile-header-actions">
        <mobile-menu
          v-if="!isLoggedIn"
          :is-logged-in="isLoggedIn"
          :show-on-desktop="false"
        />
      </div>
        
    </v-app-bar>



    <!-- Mobile mainMenu -->
    <v-navigation-drawer
      v-model="drawer"
      clipped
      app
      temporary
      width="280"
    >
    <v-list
        nav
        dense
    >
        <!-- Plain list items, not a v-list-item-group: the group's selection took the first tap, so a
             link needed two (Ansis, 2026-10-26). -->
        <v-list-item
            v-for="item in menuItemsMain"
            :key="item.title"
            link
            :to="getLocalisedUrl(item.link)"
            exact
            class="sidebar-menu-item"
        >
            <v-list-item-content>
            <v-list-item-title class="sidebar-text">{{ item.title }}</v-list-item-title>
            </v-list-item-content>
        </v-list-item>


        <v-divider class="mobile-divider"></v-divider>

        <!-- Contact Section -->
        <v-list-item
          link
          :href="supportEmailHref"
          class="sidebar-menu-item"
        >
          <v-list-item-content>
            <v-list-item-title class="sidebar-text">{{ supportEmail }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
    </v-list>
    </v-navigation-drawer>
    
    <v-main>
      <router-view :key="$route.fullPath" />
    </v-main>

    <app-footer />
  </div>
</template>
<script>
import urlUtil from '@/js/utils/urlUtil'
import AppLogo from '@/components/Logo'
import AppFooter from '@/components/Footer.vue'
import MobileMenu from '@/components/private/layout/MobileMenu.vue'
import UserAccountMenu from '@/components/private/layout/UserAccountMenu.vue'
import { SUPPORTED_LOCALES } from '@/locales/locales'
import '@/assets/css/website.css'
import { scrollToTopIfAlreadyOn } from '@/domains/website/websiteLinks'
import { MainPath } from '@/router/mainPaths'
import { SUPPORT_EMAIL } from '@/js/constants/contactDetails'

export default {
  name: 'MainLayout',
  components: {
    AppLogo,
    AppFooter,
    MobileMenu,
    UserAccountMenu
  },
  data () {
    return {
      drawer: false,
      supportEmail: SUPPORT_EMAIL,
      supportEmailHref: `mailto:${SUPPORT_EMAIL}`,
      languages: SUPPORTED_LOCALES.map(lang => ({
        value: lang.code,
        text: lang.name
      }))
    }
  },
  computed: {
    isMobileView: function() {
      return this.$vuetify && this.$vuetify.breakpoint && this.$vuetify.breakpoint.smAndDown
    },
    languge: function() {
      return this.$i18n.locale
    },
    isLoggedIn: function() {
      return this.$store.getters['user/isLoggedIn']
    },
    userOrganizations: function() {
      return this.$store.getters['dashboard/activeOrganization']
    },
    userName: function() {
      const user = this.$store.getters['user/user']
      return user && user.name ? user.name : null
    },
    organizationName: function() {
      const organization = this.$store.getters['dashboard/activeOrganization']
      return organization && organization.name ? organization.name : null
    },
    menuItemsMain: function() {
      return [
        {
          title: this.$t('layout.menu.homepage'),
          link: '/' + MainPath.HOME,
          icon: 'mdi-home'
        },
        {
          title: this.$t('layout.menu.individuals'),
          link: '/' + MainPath.PRICING,
          icon: 'mdi-account'
        },
        {
          title: this.$t('layout.menu.organizations'),
          link: '/' + MainPath.PRICING_ORGANIZATION,
          icon: 'mdi-account-group'
        },
        {
          title: this.$t('layout.menu.faq'),
          link: '/' + MainPath.FAQ,
          icon: 'mdi-frequently-asked-questions'
        }
      ]
    }
  },
  watch: {
    // Close the mobile menu once the visitor has gone somewhere.
    $route () {
      this.drawer = false
    }
  },
  created: function(){
    if (this.isLoggedIn){
      this.$store.dispatch('user/refreshUserData')
    }
  },
  methods:{
    ...urlUtil,
    onLogoClick () {
      scrollToTopIfAlreadyOn(this.$route, this.getLocalisedUrl('/'))
    },
    logout: function(){
      this.$store.dispatch('user/logout')
    },
    goToAuth: function(type) {
      if (this.isLoggedIn) {
        this.$router.push(this.getLocalisedMyPagesUrl())
      } else if (type === 'signup') {
        this.$router.push(this.getLocalisedUrl('/signup'))
      } else if (type === 'signin') {
        this.$router.push(this.getLocalisedUrl('/login'))
      }
    },
    goToMyPages: function() {
      this.$router.push(this.getLocalisedMyPagesUrl())
    },
    changeLang: function(lang) { 
      localStorage.setItem('lang', lang)
      const urlInDiffLanguage = urlUtil.getUrlInLocale(lang, urlUtil.getUrlWithoutLocale())
      window.open(urlInDiffLanguage, "_self")
    }
  }

};
</script>
<style>
.v-application .home-icon a {
  color: #eee;
  text-decoration: none;
  font-size: 22px;
}
.v-application.as-page .as-header a {
  color: #eee;
  text-decoration: none;
}
.v-application .lang-menu {
  cursor: pointer;
}
.v-application.as-page .as-header .as-organization-link {
  color: var(--color-pallet-1) !important;
  font-weight: 600;
  font-size: 24px
}
.v-application.as-page .as-header .as-organization-link .v-avatar {
  display: inline;
}

/* Navigation Drawer Styles */
.sidebar-menu-item {
  margin: 4px 0 !important;
  border-radius: 8px !important;
  transition: all 0.2s ease !important;
  cursor: pointer !important;
  user-select: none !important;
  min-height: 44px !important;
  position: relative;
  padding-left: 16px !important;
}

.sidebar-menu-item:hover {
  background: rgba(255, 255, 255, 0.1) !important;
  transform: translateX(4px) !important;
}

.sidebar-text {
  font-weight: 500 !important;
  color: white !important;
}

/* Mobile Divider */
.mobile-divider {
  border-color: rgba(255, 255, 255, 0.1) !important;
  margin: 16px 0;
}

/* Auth button styling */
.signup-btn {
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0.5px !important;
  box-shadow: 0 2px 8px rgba(46, 139, 87, 0.3) !important;
  transition: all 0.3s ease !important;
}

.signup-btn:hover {
  box-shadow: 0 4px 12px rgba(46, 139, 87, 0.4) !important;
  transform: translateY(-1px) !important;
}

/* Mobile: account button (no chevron) + three-dot menu */
.as-mobile-header-actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

/* Active language styling */
.as-active-language {
  background: var(--color-pallet-1) !important;
  color: white !important;
}

/* Desktop User Menu Styling - matches mobile menu */
.desktop-user-menu {
  min-width: 280px;
  padding: 16px;
}

.desktop-menu-section {
  padding: 0 !important;
  margin-bottom: 16px;
}

.desktop-menu-section:last-child {
  margin-bottom: 0;
}

.desktop-account-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.desktop-account-btn {
  justify-content: flex-start !important;
  padding: 12px 16px !important;
  border-radius: 8px !important;
  transition: all 0.2s ease !important;
}

.desktop-account-btn:hover {
  background: rgba(46, 139, 87, 0.1) !important;
}

.desktop-logout-btn {
  color: #dc3545 !important;
}

.desktop-logout-btn:hover {
  background: rgba(220, 53, 69, 0.1) !important;
}
</style>